import type { TariffHistoryItem, TariffStatus } from '../../../apis/types'

export const TARIFF_RATE_OPEN_END_DATE = '2030-12-31'

function dateOnly(value: string): string {
    return value.slice(0, 10)
}

export function isCurrentActiveHistory(
    history: Pick<TariffHistoryItem, 'status' | 'endDate'>,
    today = new Date()
): boolean {
    if (history.status !== 'ACTIVE' && history.status !== 'APPROVED') return false
    if (history.endDate == null) return true
    return dateOnly(history.endDate) >= dateOnly(today.toISOString())
}

export function isEmptyRateWindow(effectiveDate: string, endDate: string | null | undefined): boolean {
    if (endDate == null) return false
    return dateOnly(endDate) < dateOnly(effectiveDate)
}

export function isOpenRateEnd(endDate: string | null | undefined): boolean {
    if (endDate == null) return true
    return dateOnly(endDate) === TARIFF_RATE_OPEN_END_DATE
}

/** Current Active, else newest Pending, else newest overall. */
export function pickSelectedHistoryCuid(
    histories: TariffHistoryItem[],
    requested?: string
): string | undefined {
    if (histories.length === 0) return undefined
    if (requested != null && histories.some((h) => h.cuid === requested)) {
        return requested
    }
    const currentActive = sortHistoriesNewestFirst(
        histories.filter((h) => isCurrentActiveHistory(h))
    )
    if (currentActive[0] != null) return currentActive[0].cuid
    const pending = sortHistoriesNewestFirst(
        histories.filter((h) => h.status === 'PENDING' || h.status === 'WAITING_FOR_APPROVAL')
    )
    if (pending[0] != null) return pending[0].cuid
    return sortHistoriesNewestFirst(histories)[0]?.cuid
}

/** Rateset used for the Latest badge: newest effective date. */
export function getLatestHistoryCuid(histories: TariffHistoryItem[]): string | undefined {
    return sortHistoriesNewestFirst(histories)[0]?.cuid
}

export function sortHistoriesNewestFirst(histories: TariffHistoryItem[]): TariffHistoryItem[] {
    return [...histories].sort(
        (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
    )
}

export function isPendingRatesetStatus(status: TariffStatus | string): boolean {
    return status === 'PENDING' || status === 'WAITING_FOR_APPROVAL'
}

export function isActiveRatesetStatus(status: TariffStatus | string): boolean {
    return status === 'ACTIVE' || status === 'APPROVED'
}

export function isInactiveRatesetStatus(status: TariffStatus | string): boolean {
    return status === 'INACTIVE' || status === 'ARCHIVED'
}

export function formatRateWindowDate(value: string | null | undefined): string {
    if (value == null || value === '') return ''
    return dateOnly(value)
}

function rateEndDisplay(effectiveDate: string, endDate: string | null | undefined): string {
    if (endDate == null || isOpenRateEnd(endDate)) return 'Open'
    if (effectiveDate !== '' && isEmptyRateWindow(effectiveDate, endDate)) return 'Set on activation'
    return dateOnly(endDate)
}

/**
 * Rate add/edit dates follow the rateset window. Pending and Inactive stay
 * empty until activation. Active uses the shared sibling end when every
 * existing rate agrees, else the history end, else Open.
 */
export function lockedRateWindow(args: {
    historyEffectiveDate?: string | null
    historyEndDate?: string | null
    historyStatus?: TariffStatus | string
    siblingEndDates?: Array<string | null | undefined>
}): { effectiveDate: string; endLabel: string; endDate: string | undefined } {
    const effectiveDate = formatRateWindowDate(args.historyEffectiveDate)
    const status = args.historyStatus
    if (
        status != null &&
        (isPendingRatesetStatus(status) || isInactiveRatesetStatus(status))
    ) {
        return { effectiveDate, endLabel: 'Set on activation', endDate: undefined }
    }

    const siblingLabels = (args.siblingEndDates ?? []).map((end) => rateEndDisplay(effectiveDate, end))
    const uniqueSibling = new Set(siblingLabels)
    if (uniqueSibling.size === 1) {
        const label = siblingLabels[0] ?? 'Open'
        return {
            effectiveDate,
            endLabel: label,
            endDate: label === 'Open' || label === 'Set on activation' ? undefined : label,
        }
    }

    const historyEnd = formatRateWindowDate(args.historyEndDate)
    if (historyEnd !== '' && !isOpenRateEnd(historyEnd)) {
        return { effectiveDate, endLabel: historyEnd, endDate: historyEnd }
    }
    return { effectiveDate, endLabel: 'Open', endDate: undefined }
}
